import React, { useState, useEffect, useCallback, useRef } from 'react';
import IpTable from '../components/ipAddresses/IpTable';
import IpForm from '../components/ipAddresses/IpForm';
import IpBulkForm from '../components/ipAddresses/IpBulkForm';
import IpAddressActions from '../components/ipAddresses/IpAddressActions';
import IpViewModal from '../components/ipAddresses/IpViewModal';
import useIpAddresses from '../hooks/useIpAddresses';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useErrorDialog } from '../context/errorDialogContext';
import { exportToXlsx } from '../utils/xlsxExport';
import { useTranslation } from 'react-i18next';

/**
 * IpAddressesPage
 * - Page to manage IP whitelist: list, add, bulk add, edit, activate/deactivate and export.
 */
const IpAddressesPage = () => {
  const { t } = useTranslation();
  const {
    ipAddresses,
    loading,
    error,
    totalPages,
    totalCount,
    addIpAddress,
    addBulkIpAddresses,
    updateIpAddress,
    deleteIpAddress,
    activateIpAddress,
    deactivateIpAddress,
    fetchIpAddresses,
    clearError,
    getIpAddressById,
  } = useIpAddresses();

  const PAGE_LIMIT = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [committedSearch, setCommittedSearch] = useState('');
  const searchDebounceRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [editingIp, setEditingIp] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingIp, setViewingIp] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewingIpId, setViewingIpId] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const strings = t('ipAddresses', { returnObjects: true });
  const { showError } = useErrorDialog();

  /**
   * Resolve a friendly id/label for a given ip object or id value.
   * - Accepts either an object (ip) or an identifier; tries to find a matching entry
   *   in `ipAddresses` to produce a human label for dialogs.
   */
  const resolveIpDetails = (ipOrId) => {
    if (ipOrId && typeof ipOrId === 'object') {
      return {
        id: ipOrId.id ?? ipOrId.pk ?? ipOrId._id ?? null,
        label: ipOrId.ip_address || ipOrId.description || strings.table.copy.noDescription,
      };
    }

    const candidateId = ipOrId ?? null;
    const match = ipAddresses.find((item) =>
      [item.id, item.pk, item._id].some((identifier) => identifier === candidateId)
    );

    return {
      id: candidateId,
      label: match?.ip_address || match?.description || strings.table.copy.noDescription,
    };
  };

  /** Open confirmation for deleting a single IP */
  const openDeleteConfirm = (ipOrId) => {
    const { id, label } = resolveIpDetails(ipOrId);
    if (!id) {
      console.error('Unable to open delete confirmation without a valid id', ipOrId);
      return;
    }

    setConfirmState({
      type: 'delete',
      id,
      title: strings.confirm.delete.title,
      message: strings.confirm.delete.message.replace('{ip}', label),
      confirmLabel: strings.confirm.delete.confirmLabel,
      cancelLabel: strings.confirm.buttons.cancel,
      confirmVariant: 'danger',
    });
  };

  /** Open confirmation for deactivating a single IP */
  const openDeactivateConfirm = (ipOrId) => {
    const { id, label } = resolveIpDetails(ipOrId);
    if (!id) {
      console.error('Unable to open deactivate confirmation without a valid id', ipOrId);
      return;
    }

    setConfirmState({
      type: 'deactivate',
      id,
      title: strings.confirm.deactivate.title,
      message: strings.confirm.deactivate.message.replace('{ip}', label),
      confirmLabel: strings.confirm.deactivate.confirmLabel,
      cancelLabel: strings.confirm.buttons.cancel,
      confirmVariant: 'danger',
    });
  };

  /** Cancel and close the confirm dialog */
  const handleCancelConfirm = () => {
    setConfirmState(null);
  };

  /** Execute the confirmed delete/deactivate action */
  const handleConfirmAction = async () => {
    if (!confirmState) return;
    setConfirmLoading(true);
    try {
      if (confirmState.type === 'delete') {
        await handleDeleteIp(confirmState.id);
      } else if (confirmState.type === 'deactivate') {
        await handleDeactivateIp(confirmState.id);
      }
      setConfirmState(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Handle adding new IP address
  /** Add a single IP and close the form on success */
  const handleAddIp = async (ipData) => {
    setActionLoading(true);
    try {
      const result = await addIpAddress(ipData);
      if (result.success) {
        setShowForm(false);
        // You might want to show a success message here
        console.log('IP address added successfully');
      }
    } catch (err) {
      console.error('Failed to add IP address:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk adding IP addresses
  /** Bulk add many IPs; refreshes list internally in the hook */
  const handleBulkAddIps = async (bulkData) => {
    setActionLoading(true);
    try {
      const result = await addBulkIpAddresses(bulkData);
      if (result.success) {
        setShowBulkForm(false);
        console.log('Bulk IP addresses added successfully');
      }
    } catch (err) {
      console.error('Failed to add bulk IP addresses:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle editing IP address
  /** Open edit form for a given IP object */
  const handleEditIp = (ip) => {
    setEditingIp(ip);
    setShowForm(true);
  };

  // Handle updating IP address
  /** Send updated IP data to API and close the form on success */
  const handleUpdateIp = async (ipData) => {
    if (!editingIp) return;
    
    setActionLoading(true);
    try {
      const result = await updateIpAddress(editingIp.id, ipData);
      if (result.success) {
        setShowForm(false);
        setEditingIp(null);
        console.log('IP address updated successfully');
      }
    } catch (err) {
      console.error('Failed to update IP address:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle deleting IP address - accept either the ip object or an id
  /** Delete an IP (accepts object or id) and refresh local state via hook */
  const handleDeleteIp = async (ipOrId) => {
    setActionLoading(true);
    try {
      const id = typeof ipOrId === 'object'
        ? (ipOrId.id ?? ipOrId.pk ?? ipOrId._id)
        : ipOrId;

      if (!id) {
        console.error('handleDeleteIp called without a valid id:', ipOrId);
        setActionLoading(false);
        return;
      }

      const result = await deleteIpAddress(id);
      if (result.success) {
        console.log('IP address deleted successfully');
      }
    } catch (err) {
      console.error('Failed to delete IP address:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle activating IP address - accept ip object or id
  /** Activate an IP (accepts object or id) using the hook's optimistic update flow */
  const handleActivateIp = async (ipOrId) => {
    setActionLoading(true);
    try {
      const id = typeof ipOrId === 'object' ? (ipOrId.id ?? ipOrId.pk ?? ipOrId._id) : ipOrId;
      if (!id) {
        console.error('handleActivateIp called without a valid id:', ipOrId);
        setActionLoading(false);
        return;
      }
      const result = await activateIpAddress(id);
      if (result.success) {
        console.log('IP address activated successfully');
      }
    } catch (err) {
      console.error('Failed to activate IP address:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle deactivating IP address - accept ip object or id
  /** Deactivate an IP (accepts object or id) using the hook's optimistic update flow */
  const handleDeactivateIp = async (ipOrId) => {
    setActionLoading(true);
    try {
      const id = typeof ipOrId === 'object' ? (ipOrId.id ?? ipOrId.pk ?? ipOrId._id) : ipOrId;
      if (!id) {
        console.error('handleDeactivateIp called without a valid id:', ipOrId);
        setActionLoading(false);
        return;
      }
      const result = await deactivateIpAddress(id);
      if (result.success) {
        console.log('IP address deactivated successfully');
      }
    } catch (err) {
      console.error('Failed to deactivate IP address:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle viewing IP details - accept ip object or id
  /** View IP details in a modal; fetches latest details if only an id is provided */
  const handleViewIp = async (ipOrId) => {
    const id = typeof ipOrId === 'object' ? (ipOrId.id ?? ipOrId.pk ?? ipOrId._id) : ipOrId;

    if (!id && id !== 0) {
      console.error('handleViewIp called without a valid id:', ipOrId);
      return;
    }

    if (typeof ipOrId === 'object') {
      setViewingIp(ipOrId);
    } else {
      setViewingIp(null);
    }

    setViewingIpId(id);
    setShowViewModal(true);
    setViewLoading(true);

    try {
      const response = await getIpAddressById(id, { withLoading: false });
      if (response) {
        setViewingIp(response);
      }
    } catch (err) {
      console.error('Failed to fetch IP details:', err);
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingIp(null);
    setViewingIpId(null);
    setViewLoading(false);
  };

  const loadIpAddresses = useCallback((isRefresh = false, search = committedSearch) => {
    fetchIpAddresses({
      skip: (currentPage - 1) * PAGE_LIMIT,
      limit: PAGE_LIMIT,
      ...(search ? { search } : {}),
      force: isRefresh,
    });
  }, [fetchIpAddresses, currentPage, committedSearch]);

  useEffect(() => {
    loadIpAddresses();
  }, [loadIpAddresses]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      await loadIpAddresses(true);
    } catch (err) {
      console.error('Failed to refresh IP addresses:', err);
    } finally {
      setRefreshLoading(false);
    }
  };

  // Handle form cancellation
  const handleCancelForm = () => {
    setShowForm(false);
    setShowBulkForm(false);
    setEditingIp(null);
  };

  // Handle CSV export
  const handleExportCSV = () => {
    const headers = [
      { label: 'IP Address', key: 'ip_address' },
      { label: 'Description', key: 'description' },
      { label: t('commonCols.status'), key: 'status' },
    ];

    const data = ipAddresses.map(ip => ({
      ...ip,
      status: ip.is_active ? 'Active' : 'Inactive'
    }));

    exportToXlsx(data, headers, 'ip_addresses');
  };

  // Show global error dialog when error exists
  React.useEffect(() => {
    if (error) {
      showError(error, strings.page.alerts?.errorTitle || 'Error');
      clearError();
    }
  }, [error, showError, clearError, strings.page.alerts]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{strings.page.header.title}</h1>
            <p className="mt-2 text-gray-600">
              {strings.page.header.subtitle}
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex space-x-4">
            <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">{strings.page.stats.total}</div>
                <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
              </div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">{strings.page.stats.active}</div>
                <div className="text-2xl font-bold text-green-600">{ipAddresses.filter(ip => ip.is_active).length}</div>
              </div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">{strings.page.stats.inactive}</div>
                <div className="text-2xl font-bold text-red-600">{ipAddresses.filter(ip => !ip.is_active).length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global error dialog will show errors via ErrorDialogProvider */}

      {/* Actions */}
      <IpAddressActions
        onAddIp={() => setShowForm(true)}
        onBulkAdd={() => setShowBulkForm(true)}
        onRefresh={handleRefresh}
        onExport={handleExportCSV}
        searchValue={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
          searchDebounceRef.current = setTimeout(() => {
            setCommittedSearch(val);
            setCurrentPage(1);
          }, 400);
        }}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        loading={actionLoading}
        refreshLoading={refreshLoading}
        ipAddresses={ipAddresses}
      />

      {/* Forms */}
      {(showForm || showBulkForm) && (
        <div className="space-y-6">
          {showForm && (
            <IpForm
              ip={editingIp}
              onSave={editingIp ? handleUpdateIp : handleAddIp}
              onCancel={handleCancelForm}
              loading={actionLoading}
            />
          )}
          {showBulkForm && (
            <IpBulkForm
              onSave={handleBulkAddIps}
              onCancel={handleCancelForm}
              loading={actionLoading}
            />
          )}
        </div>
      )}

      {/* IP Addresses Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
        <IpTable
          ipAddresses={ipAddresses}
          loading={loading}
          onEdit={handleEditIp}
          onDelete={openDeleteConfirm}
          onActivate={handleActivateIp}
          onDeactivate={openDeactivateConfirm}
          onView={handleViewIp}
          viewingId={viewingIpId}
          viewLoading={viewLoading}
          onExport={handleExportCSV}
          searchValue={searchTerm}
          statusFilter={statusFilter}
          onClearFilters={handleClearFilters}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
          style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
        >
          <span className="text-sm" style={{ color: '#6b7280' }}>
            Page {currentPage} of {totalPages} — {totalCount} total
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
              className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                acc.push(p);
                return acc;
              }, [])
              .map((item) =>
                typeof item === 'string' ? (
                  <span key={item} className="px-2 text-sm" style={{ color: '#9ca3af' }}>...</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    disabled={loading}
                    className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                      color: item === currentPage ? '#fff' : '#374151',
                    }}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
              className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: '#0066cc', color: '#fff' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <IpViewModal
        isOpen={showViewModal}
        onClose={handleCloseViewModal}
        ip={viewingIp}
        loading={viewLoading}
      />

      {confirmState && (
        <ConfirmDialog
          isOpen
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          cancelLabel={confirmState.cancelLabel}
          confirmVariant={confirmState.confirmVariant}
          loading={confirmLoading}
          loadingLabel={confirmState.confirmLabel}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelConfirm}
        />
      )}
    </div>
  );
};

export default IpAddressesPage;